'use client';

import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useLazyQuery } from '@parkchirp/api-client';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useDebounce } from '../../hooks';
import { LIST_PROMO_CODES_BY_FACILITY_ID_V2 } from '../../queries';
import { HourlyQueryContextEnum, ZonePromoCode } from '../../types';

type Option = ZonePromoCode & {
	label: string;
	value: string;
	key: string;
};

interface ZonePromoCodeAutocompleteProps {
	facilityId: string;
	zonePromoCodeId?: number;
	zonePromoCodeText?: string;
	zoneName: string;
	rateName?: string;
	label?: string;
	disabled?: boolean;
	disableClearable?: boolean;
	error?: boolean;
	required?: boolean;
	onChange?: (value: Option) => void;
	loadingDependency?: boolean;
	ignoredOptions?: string[];
	size?: 'small' | 'medium';
}

export const ZonePromoCodeAutocomplete: FC<ZonePromoCodeAutocompleteProps> = ({
	facilityId,
	zonePromoCodeId,
	zonePromoCodeText,
	zoneName,
	rateName,
	label,
	disabled = false,
	disableClearable = false,
	error = false,
	required = false,
	onChange,
	loadingDependency = false,
	ignoredOptions = [],
	size = 'medium',
}) => {
	const defaultOption: Partial<Option> = useMemo(() => {
		if (!facilityId || !zonePromoCodeId) {
			return null;
		}

		return {
			label: zonePromoCodeText,
			value: String(zonePromoCodeId),
			key: String(zonePromoCodeId),
			zoneName,
			rateName,
			text: zonePromoCodeText,
		};
	}, [zonePromoCodeId, zonePromoCodeText, zoneName, rateName, facilityId]);

	const [options, setOptions] = useState<Option[]>(defaultOption ? [defaultOption as Option] : []);

	const [searchTerm, setSearchTerm] = useState('');
	const [open, setOpen] = useState(false);
	const [selectedOption, setSelectedOption] = useState<Option>(defaultOption as Option);

	useEffect(() => {
		setSelectedOption(defaultOption as Option);
	}, [defaultOption]);

	const debouncedOnChange = useDebounce(setSearchTerm, 800);

	const onInputChange = useCallback(
		(value: string) => {
			if (open && value !== searchTerm) {
				debouncedOnChange(value);
			}
		},
		[open, searchTerm, debouncedOnChange],
	);

	const onAutocompleteChange = useCallback(
		(option: Option) => {
			if (!option) {
				setSearchTerm(null);
			}
			setSelectedOption(option);
			onChange(option);
		},
		[onChange],
	);

	const queryFilters = useMemo(() => {
		if (!searchTerm) {
			return [];
		}

		return [
			{
				propertyName: 'text',
				op: 'contains',
				value: `${searchTerm}`,
			},
			{
				propertyName: 'rateName',
				op: 'contains',
				value: `${searchTerm}`,
			},
		];
	}, [searchTerm]);

	const [search, { loading }] = useLazyQuery(LIST_PROMO_CODES_BY_FACILITY_ID_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'no-cache',
	});

	useEffect(() => {
		if (!open && !selectedOption) {
			setSearchTerm(null);
		}
	}, [open, selectedOption]);

	useEffect(() => {
		search({
			variables: {
				offset: 0,
				limit: 50,
				queryContext: HourlyQueryContextEnum.AUTOCOMPLETE,
				filters: queryFilters,
				facilityId,
				order: {
					field: 'text',
					sort: 'asc',
				},
			},
			onCompleted: (data) => {
				const newOptions: Option[] = data.listPromoCodesByFacilityId.reduce((acc, zonePromoCode) => {
					acc.push({
						...zonePromoCode,
						label: zonePromoCode.text,
						value: zonePromoCode.id,
						key: zonePromoCode.id,
					});

					return acc;
				}, []);
				setOptions(newOptions);
			},
		});
	}, [search, queryFilters, facilityId]);

	const isLoading = loading || loadingDependency;

	return (
		<Autocomplete
			open={open}
			onOpen={() => {
				setOpen(true);
			}}
			onClose={() => {
				setOpen(false);
			}}
			value={selectedOption}
			onChange={(_, value) => {
				onAutocompleteChange(value);
			}}
			onInputChange={(_, value, reason) => {
				if (reason === 'input') {
					onInputChange(value);
				}
			}}
			options={options}
			getOptionDisabled={(option) => ignoredOptions.includes(option.value)}
			isOptionEqualToValue={(option, { value }) => option.value === value}
			loading={isLoading}
			filterOptions={(x) => x}
			forcePopupIcon={false}
			fullWidth
			renderOption={(props, option) => (
				<li {...props} key={option.key} style={{ whiteSpace: 'nowrap' }}>
					<i key={option.key} style={{ color: '#aaa', marginRight: '5px' }}>
						{option.zoneName}
					</i>
					{` ${option.label} - ${option.rateName}`}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label || ''}
					required={required}
					error={error}
					placeholder='Start Typing...'
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color='inherit' size={20} data-cy='loading-indicator' />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
			disabled={disabled}
			readOnly={disabled}
			disableClearable={disableClearable}
			sx={{
				'& .MuiInputBase-root': {
					paddingRight: '39px !important',
				},
			}}
			size={size}
		/>
	);
};
